import { useGetActivationsQuery } from '@/api/recalc/endpoints/activationsApiSlice';
import { useLazyGetDataQuery } from '@/api/recalc/endpoints/dataApiSlice';
import { Suspense, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  useGetActuatorPlanByPeriodQuery,
  useGetOccupancyPredictionQuery,
} from '@/api/subscription-manager/predictiveHeatControlSlice';

import { useGetBaselineDaysByIdQuery } from '@/api/recalc/endpoints/baselineApiSlice';
import { useGetUnitsByAccountQuery } from '@/api/recalc/endpoints/unitsApiSlice';

import { Box, Stack } from '@mui/material';
import { DateTime } from 'luxon';

import GatewayLoadingIndicator from '@/components/indicators/GatewayLoadingIndicator';

import TimeRangeSelector from '@/components/time-range-selector/TimeRangeSelector';
import ControlTypeDisplay from '@/features/building-optimization/components/ControlTypeDisplay';
import SynchronizedHeatingCharts from '@/features/building-optimization/components/highcharts/SynchronizedHeatingCharts';

function formatAndEncodeTimestampForAPI(date) {
  const isoString = DateTime.fromJSDate(date).toISO();
  return encodeURIComponent(isoString);
}

function formatAndEncodeTimestampForSubscriptionManagerAPI(date) {
  return DateTime.fromJSDate(date)
    .toUTC()
    .toISO({ suppressMilliseconds: true });
}

function buildActivationRangesWithOff(activations, channelKeyInput) {
  if (!activations || activations.length === 0) return [];

  // Normalize channel key
  let channelKey;
  if (typeof channelKeyInput === 'string') {
    channelKey =
      channelKeyInput.charAt(0).toUpperCase() + channelKeyInput.slice(1);
  } else {
    channelKey = `Output${channelKeyInput}`;
  }

  const sorted = [...activations].sort(
    (a, b) => new Date(a.Timestamp) - new Date(b.Timestamp),
  );

  const ranges = [];
  let lastValue = null;
  let periodStart = new Date(sorted[0].Timestamp).getTime();

  sorted.forEach((entry, i) => {
    const outputs = JSON.parse(entry.Outputs || '{}');
    const value = parseInt(outputs[channelKey], 10);
    const ts = new Date(entry.Timestamp).getTime();

    // When the value changes, close the previous period
    if (lastValue !== null && value !== lastValue) {
      ranges.push({
        x: periodStart,
        x2: ts,
        y: 0,
        on: lastValue === 100,
      });
      periodStart = ts;
    }

    lastValue = value;

    // Close at the end
    if (i === sorted.length - 1) {
      ranges.push({
        x: periodStart,
        x2: ts,
        y: 0,
        on: lastValue === 100,
      });
    }
  });

  return ranges;
}

export default function PredictiveHeatControlPage() {
  const location = useLocation();
  const selectedSubscription = location.state?.subscription;

  const { data: units, isLoading: isLoadingUnits } = useGetUnitsByAccountQuery(
    selectedSubscription?.AccountId,
    {
      skip: !selectedSubscription?.AccountId,
    },
  );

  const [timeRange, setTimeRange] = useState({
    start: DateTime.local().minus({ days: 14 }).toUTC().toMillis(),
    end: DateTime.local().toUTC().toMillis(),
  });

  const [getData, { isLoading: isLoadingData, isFetching: isFetchingData }] =
    useLazyGetDataQuery();

  const {
    data: occupancyPrediction,
    isLoading: isLoadingOccupancyPrediction,
    isFetching: isFetchingOccupancyPrediction,
  } = useGetOccupancyPredictionQuery(
    {
      jobSubscriptionId: selectedSubscription?.JobSubscriptionId,
      start: formatAndEncodeTimestampForAPI(new Date(timeRange.start)),
      end: formatAndEncodeTimestampForAPI(new Date(timeRange.end)),
    },
    {
      skip: !selectedSubscription || !selectedSubscription.JobSubscriptionId,
    },
  );

  const {
    data: baselineDays,
    isLoading: isLoadingBaselineDays,
    isFetching: isFetchingBaselineDays,
  } = useGetBaselineDaysByIdQuery(
    {
      subscriptionId: selectedSubscription?.JobSubscriptionId,
      from: formatAndEncodeTimestampForAPI(new Date(timeRange.start)),
    },
    {
      skip: !selectedSubscription || !selectedSubscription.JobSubscriptionId,
    },
  );

  const baselineRanges = (baselineDays || []).map((d) => ({
    x: new Date(d.Start).getTime(),
    x2: new Date(d.End).getTime(),
    y: 1,
    isBaseline: true, // extra flag to identify baseline days
  }));

  const occupancyRanges = (occupancyPrediction?.occupancy_plan || []).map(
    (entry) => ({
      x: new Date(entry.interval_start).getTime(),
      x2: new Date(entry.interval_end).getTime(),
      y: 2,
      isOccupancy: true,
      on: entry.label === true, // store it for later use
    }),
  );

  const {
    data: activationsECL,
    isLoading: isLoadingActivationsECL,
    isFetching: isFetchingActivationsECL,
  } = useGetActuatorPlanByPeriodQuery(
    {
      id: selectedSubscription?.JobSubscriptionId,
      start_time: formatAndEncodeTimestampForSubscriptionManagerAPI(
        new Date(timeRange.start),
      ),
      end_time: formatAndEncodeTimestampForSubscriptionManagerAPI(
        new Date(timeRange.end),
      ),
    },
    {
      skip:
        !selectedSubscription ||
        selectedSubscription.JobType !== 'PredictiveHeatControl' ||
        selectedSubscription.UserInput?.ControlType === 'pct',
    },
  );

  const {
    data: activationsPCT,
    isLoading: isLoadingActivationsData,
    isFetching: isFetchingActivationsPCT,
  } = useGetActivationsQuery(
    {
      from: formatAndEncodeTimestampForAPI(new Date(timeRange.start)),
      to: formatAndEncodeTimestampForAPI(new Date(timeRange.end)),
      gatewayId: selectedSubscription?.UserInput?.GatewayId,
    },
    {
      skip:
        !selectedSubscription ||
        selectedSubscription.JobType !== 'PredictiveHeatControl' ||
        selectedSubscription.UserInput?.ControlType === 'ecl' ||
        selectedSubscription.UserInput?.ControlType === 'bravida',
    },
  );

  const [heatingPipeTemp, setHeatingPipeTemp] = useState([]);
  const [coldWaterTemp, setColdWaterTemp] = useState([]);
  const [comfortTemps, setComfortTemps] = useState([]);
  const [co2Series, setCo2Series] = useState([]);
  const [powerConsumptionSeries, setPowerConsumptionSeries] = useState([]);
  const [waterConsumptionSeries, setWaterConsumptionSeries] = useState([]);
  const [relativeHumiditySeries, setRelativeHumiditySeries] = useState([]);
  const [xAxisRange, setXAxisRange] = useState({ min: null, max: null });
  const [activationRanges, setActivationRanges] = useState([]);

  useEffect(() => {
    const ranges = [];

    if (
      selectedSubscription?.UserInput?.ControlType === 'pct' &&
      activationsPCT &&
      selectedSubscription?.UserInput?.Channel !== undefined
    ) {
      const pctRanges = buildActivationRangesWithOff(
        activationsPCT,
        selectedSubscription.UserInput.Channel,
      );
      ranges.push(...pctRanges);
    }

    // For ECL-based activations
    if (
      selectedSubscription?.UserInput?.ControlType === 'ecl' &&
      activationsECL?.actuator_plan?.length
    ) {
      const eclRanges = activationsECL.actuator_plan.map((entry) => ({
        x: new Date(entry.interval_start).getTime(),
        x2: new Date(entry.interval_end).getTime(),
        y: 0,
        on: entry.setpoint === 100,
      }));
      ranges.push(...eclRanges);
    }

    if (ranges.length > 0) {
      setActivationRanges(ranges);

      // Also update x-axis range to include these activations
      const allTimestamps = ranges.flatMap((r) => [r.x, r.x2]);
      setXAxisRange((prev) => ({
        min: prev.min
          ? Math.min(prev.min, ...allTimestamps)
          : Math.min(...allTimestamps),
        max: prev.max
          ? Math.max(prev.max, ...allTimestamps)
          : Math.max(...allTimestamps),
      }));
    }
  }, [activationsPCT, activationsECL, selectedSubscription, timeRange]);

  useEffect(() => {
    if (!selectedSubscription) return;

    let cancelled = false;

    const toApiArgs = (unit) => ({
      from: formatAndEncodeTimestampForAPI(new Date(timeRange.start)),
      to: formatAndEncodeTimestampForAPI(new Date(timeRange.end)),
      unitId: unit.UnitId,
      datatype: unit.DataType,
      unitTypeInputId: unit.UnitTypeInputId,
      aggregateType: 'Hour',
    });

    const buildTask = (category, unit) => {
      const unitName =
        (units.find((u) => u.UnitId === unit.UnitId) || {}).Name ||
        'Unknown Unit';

      // Wrap each call so a single failure doesn't reject the whole Promise.all
      return getData(toApiArgs(unit))
        .unwrap()
        .then((fetchedData) => ({
          category,
          unit,
          unitName,
          unitLabel: unit.Base || '',
          data: (fetchedData || []).map((p) => [
            new Date(p.Timestamp).getTime(),
            p.Value,
          ]),
        }))
        .catch((err) => {
          console.error(
            `Failed to fetch data for unit ${unit.UnitId}: ${err?.message || err}`,
          );
          return null; // swallow error for this unit
        });
    };

    const loadData = async () => {
      // 1) Build all fetch promises up front
      const tasks = Object.entries(selectedSubscription.DataInput).flatMap(
        ([category, categoryUnits]) =>
          categoryUnits.map((unit) => buildTask(category, unit)),
      );

      // 2) Run them concurrently
      const results = await Promise.all(tasks);
      if (cancelled) return;

      // 3) Bucket series by category
      const heatingPipeTemp = [];
      const coldWaterTemp = [];
      const comfort = [];
      const co2 = [];
      const powerConsumption = [];
      const waterConsumption = [];
      const relativeHumidity = [];

      const valid = results.filter(Boolean);

      valid.forEach((r) => {
        const seriesMeta = {
          name: r.unitName,
          category: r.category,
          data: r.data,
          unitLabel: r.unitLabel,
          unitInfo: r.unit,
          unitName: r.unitName,
        };

        if (
          r.category === 'HeatingPipeSupplyTemp' ||
          r.category === 'HeatingPipeReturnTemp'
        ) {
          heatingPipeTemp.push(seriesMeta);
        } else if (
          r.category === 'ColdWaterSupplyPipeTemp' ||
          r.category === 'ColdWaterSupplyAirTemp'
        ) {
          coldWaterTemp.push(seriesMeta);
        } else if (r.category === 'ComfortSensorTemp') {
          comfort.push(seriesMeta);
        } else if (r.category === 'CO2Level') {
          co2.push(seriesMeta);
        } else if (r.category === 'PowerConsumption') {
          powerConsumption.push(seriesMeta);
        } else if (r.category === 'WaterConsumption') {
          waterConsumption.push(seriesMeta);
        } else if (r.category === 'RelativeHumidity') {
          relativeHumidity.push(seriesMeta);
        }
      });

      setHeatingPipeTemp(heatingPipeTemp);
      setColdWaterTemp(coldWaterTemp);
      setComfortTemps(comfort);
      setCo2Series(co2);
      setPowerConsumptionSeries(powerConsumption);
      setWaterConsumptionSeries(waterConsumption);
      setRelativeHumiditySeries(relativeHumidity);

      // 4) Compute common x-axis range
      const allTimestamps = [
        ...heatingPipeTemp,
        ...coldWaterTemp,
        ...comfort,
        ...co2,
        ...powerConsumption,
        ...waterConsumption,
        ...relativeHumidity,
      ].flatMap((s) => s.data.map((p) => p[0]));

      if (allTimestamps.length > 0) {
        setXAxisRange({
          min: Math.min(...allTimestamps),
          max: Math.max(...allTimestamps),
        });
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [selectedSubscription, timeRange, units, getData]);

  if (!selectedSubscription) return null;

  return (
    <>
      <Suspense fallback={<div>Loading TreeComponent...</div>}>
        <GatewayLoadingIndicator
          isLoading={
            isLoadingData ||
            isFetchingData ||
            isLoadingActivationsData ||
            isFetchingActivationsPCT ||
            isLoadingActivationsECL ||
            isFetchingActivationsECL ||
            isLoadingBaselineDays ||
            isFetchingBaselineDays ||
            isLoadingOccupancyPrediction ||
            isFetchingOccupancyPrediction ||
            isLoadingUnits
          }
        />
      </Suspense>
      <Stack direction="column" spacing={2}>
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <Suspense fallback={<div>Loading control type...</div>}>
            <ControlTypeDisplay
              controlType={selectedSubscription.UserInput?.ControlType}
            />
          </Suspense>

          {/* <Box display="flex" alignItems="center"> */}
          <Suspense fallback={<div>Loading time range selector...</div>}>
            <TimeRangeSelector
              timeRange={timeRange}
              setTimeRange={setTimeRange}
            />
          </Suspense>
          {/* </Box> */}
        </Box>
        {units && units.length > 0 && (
          <Suspense fallback={<div>Loading charts...</div>}>
            <SynchronizedHeatingCharts
              heatingPipeTemp={heatingPipeTemp}
              coldWaterTemp={coldWaterTemp}
              comfortTemps={comfortTemps}
              co2Series={co2Series}
              powerConsumptionSeries={powerConsumptionSeries}
              waterConsumptionSeries={waterConsumptionSeries}
              relativeHumiditySeries={relativeHumiditySeries}
              activationRanges={activationRanges}
              baselineRanges={baselineRanges}
              occupancyRanges={occupancyRanges}
              xAxisRange={xAxisRange}
              loading={
                isLoadingData ||
                isFetchingData ||
                isLoadingActivationsData ||
                isFetchingActivationsPCT ||
                isLoadingActivationsECL ||
                isFetchingActivationsECL ||
                isLoadingBaselineDays ||
                isFetchingBaselineDays ||
                isLoadingOccupancyPrediction ||
                isFetchingOccupancyPrediction
              }
            />
          </Suspense>
        )}

        {/* <Grid container spacing={2}>
            <Grid item xs={10}>
            </Grid>
            <Grid item xs={2}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Status
                </Typography>
                <PredictiveHeatControlStatusTreeView
                  subscription={selectedSubscription}
                />
              </Card>
            </Grid>
          </Grid> */}
      </Stack>
    </>
  );
}
